import { useState } from 'react';
import { CoachLayout } from '@/components/coach/layout/CoachLayout';
import { sportConfig, segmentTypeConfig } from '@/lib/coachConstants';
import type { SportType, SegmentType, DifficultyLevel } from '@/lib/coachConstants';
import {
  useWorkoutTemplates,
  useCreateWorkoutTemplate,
  useUpdateWorkoutTemplate,
  useDeleteWorkoutTemplate,
  useDuplicateWorkoutTemplate,
  useAddWorkoutSegment,
  useUpdateWorkoutSegment,
  useDeleteWorkoutSegment,
  useReorderWorkoutSegments,
} from '@/hooks/useWorkoutTemplates';
import type { WorkoutTemplateResponse, WorkoutSegmentResponse } from '@/lib/templateApi';
import { cn } from '@/lib/utils';
import { Plus, GripVertical, Trash2, Copy, ChevronDown, ChevronRight, Save, Pencil, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/feedback/alert-dialog';

const sportOptions: SportType[] = ['running', 'cycling', 'swimming', 'strength', 'crossfit', 'hyrox', 'multi-sport'];
const difficultyOptions: DifficultyLevel[] = ['beginner', 'intermediate', 'advanced', 'elite'];
const stimulusOptions = ['Endurance', 'Threshold', 'VO2max', 'Recovery', 'Strength-Endurance', 'Conditioning'];

// ── Local segment draft type (for editing before save) ──
interface SegmentDraft {
  id: string;
  type: string;
  name: string;
  duration?: string;
  distance?: string;
  intensity?: string;
  target_pace?: string;
  target_hr?: string;
  target_power?: string;
  rest?: string;
  notes?: string;
  isNew?: boolean; // not yet persisted
}

function SegmentRow({
  segment, onUpdate, onDelete, onDuplicate,
}: {
  segment: SegmentDraft; onUpdate: (s: SegmentDraft) => void; onDelete: () => void; onDuplicate: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: segment.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [expanded, setExpanded] = useState(false);
  const config = segmentTypeConfig[segment.type as SegmentType] ?? segmentTypeConfig.main;

  return (
    <div ref={setNodeRef} style={style} className={cn('rounded-lg border border-border/15 bg-card/30 transition-all', isDragging && 'opacity-50 shadow-lg')}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
          <GripVertical className="h-3.5 w-3.5" />
        </div>
        <div className="h-6 w-1 rounded-full flex-shrink-0" style={{ background: config.color }} />
        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: `${config.color}15`, color: config.color }}>
          {config.label}
        </span>
        <span className="text-xs text-foreground flex-1 min-w-0 truncate">{segment.name}</span>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground flex-shrink-0">
          {segment.duration && <span>{segment.duration}</span>}
          {segment.intensity && <span className="hidden sm:inline">{segment.intensity}</span>}
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground" onClick={onDuplicate}>
            <Copy className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={onDelete}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/10">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
            {[
              { key: 'name', label: 'Name' }, { key: 'duration', label: 'Duration' }, { key: 'distance', label: 'Distance' },
              { key: 'intensity', label: 'Intensity' }, { key: 'target_pace', label: 'Target Pace' }, { key: 'target_hr', label: 'Target HR' },
              { key: 'target_power', label: 'Target Power' }, { key: 'rest', label: 'Rest' },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{label}</label>
                <input type="text" value={(segment as unknown as Record<string, unknown>)[key] as string || ''}
                  onChange={(e) => onUpdate({ ...segment, [key]: e.target.value || undefined })}
                  className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground placeholder:text-muted-foreground/40 focus:border-accent/40 focus:outline-none transition-colors" placeholder="—" />
              </div>
            ))}
            <div className="space-y-1">
              <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Type</label>
              <select value={segment.type} onChange={e => onUpdate({ ...segment, type: e.target.value })}
                className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none capitalize">
                {(Object.keys(segmentTypeConfig) as SegmentType[]).map(t => <option key={t} value={t}>{segmentTypeConfig[t].label}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3 space-y-1">
            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Notes</label>
            <textarea value={segment.notes || ''} onChange={(e) => onUpdate({ ...segment, notes: e.target.value || undefined })}
              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground placeholder:text-muted-foreground/40 focus:border-accent/40 focus:outline-none transition-colors resize-none h-16" placeholder="Coaching notes..." />
          </div>
        </div>
      )}
    </div>
  );
}

function segmentToServerPayload(seg: SegmentDraft) {
  return {
    type: seg.type,
    name: seg.name,
    duration: seg.duration,
    distance: seg.distance,
    intensity: seg.intensity,
    target_pace: seg.target_pace,
    target_hr: seg.target_hr,
    target_power: seg.target_power,
    rest: seg.rest,
    notes: seg.notes,
  };
}

function serverSegmentToDraft(seg: WorkoutSegmentResponse): SegmentDraft {
  return {
    id: seg.id,
    type: seg.type ?? 'main',
    name: seg.name ?? '',
    duration: seg.duration ?? undefined,
    distance: seg.distance ?? undefined,
    intensity: seg.intensity ?? undefined,
    target_pace: seg.target_pace ?? undefined,
    target_hr: seg.target_hr ?? undefined,
    target_power: seg.target_power ?? undefined,
    rest: seg.rest ?? undefined,
    notes: seg.notes ?? undefined,
  };
}

export default function WorkoutBuilderPage() {
  const { data, isLoading } = useWorkoutTemplates();
  const templates = data?.items ?? [];

  const createMutation = useCreateWorkoutTemplate();
  const updateMutation = useUpdateWorkoutTemplate();
  const deleteMutation = useDeleteWorkoutTemplate();
  const duplicateMutation = useDuplicateWorkoutTemplate();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [segments, setSegments] = useState<SegmentDraft[]>([]);
  const [metaDraft, setMetaDraft] = useState<Partial<WorkoutTemplateResponse>>({});
  const [editingMeta, setEditingMeta] = useState(false);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const selected = templates.find((t) => t.id === selectedId);

  // Segment mutation hooks — need templateId
  const addSegmentMutation = useAddWorkoutSegment(selectedId ?? '');
  const updateSegmentMutation = useUpdateWorkoutSegment(selectedId ?? '');
  const deleteSegmentMutation = useDeleteWorkoutSegment(selectedId ?? '');
  const reorderMutation = useReorderWorkoutSegments(selectedId ?? '');

  const loadTemplate = (t: WorkoutTemplateResponse) => {
    setSelectedId(t.id);
    setSegments(t.segments.map(serverSegmentToDraft));
    setMetaDraft({ name: t.name, sport: t.sport, stimulus: t.stimulus ?? undefined, duration: t.duration ?? undefined, difficulty: t.difficulty ?? undefined, tags: t.tags, notes: t.notes ?? undefined });
    setEditingMeta(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = segments.findIndex((s) => s.id === active.id);
    const newIdx = segments.findIndex((s) => s.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    setSegments(arrayMove(segments, oldIdx, newIdx));
  };

  const handleCreate = async () => {
    try {
      const t = await createMutation.mutateAsync({ name: 'New Workout', sport: 'running' });
      loadTemplate(t);
      setEditingMeta(true);
      toast.success('New workout created');
    } catch {
      toast.error('Failed to create workout');
    }
  };

  const handleDuplicate = async () => {
    if (!selected) return;
    try {
      await handleSave();
      const copy = await duplicateMutation.mutateAsync(selected.id);
      loadTemplate(copy);
      toast.success(`Duplicated as "${copy.name}"`);
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    const name = selected.name;
    try {
      await deleteMutation.mutateAsync(selected.id);
      setSelectedId(null);
      setSegments([]);
      setMetaDraft({});
      toast.success(`"${name}" deleted`);
    } catch {
      toast.error('Failed to delete workout');
    }
  };

  const handleSave = async () => {
    if (!selected || !selectedId) return;
    setSaving(true);
    try {
      // 1. Update template metadata
      await updateMutation.mutateAsync({ id: selectedId, payload: {
        name: metaDraft.name ?? selected.name,
        sport: metaDraft.sport ?? selected.sport,
        stimulus: metaDraft.stimulus ?? undefined,
        duration: metaDraft.duration ?? undefined,
        difficulty: metaDraft.difficulty ?? undefined,
        tags: metaDraft.tags ?? selected.tags,
        notes: metaDraft.notes ?? undefined,
      }});

      // 2. Save new/updated segments (existing segments were updated in-place via draft)
      // For now, persist new segments that were added locally
      const newSegs = segments.filter(s => s.isNew);
      for (const seg of newSegs) {
        await addSegmentMutation.mutateAsync(segmentToServerPayload(seg));
      }
      // Update existing modified segments
      const existingSegs = segments.filter(s => !s.isNew);
      for (const seg of existingSegs) {
        await updateSegmentMutation.mutateAsync({ segmentId: seg.id, payload: segmentToServerPayload(seg) });
      }
      // Reorder if needed
      const orderedIds = segments.filter(s => !s.isNew).map(s => s.id);
      if (orderedIds.length > 1) {
        await reorderMutation.mutateAsync(orderedIds);
      }

      toast.success(`"${metaDraft.name ?? selected.name}" saved`);
    } catch {
      toast.error('Failed to save workout');
    } finally {
      setSaving(false);
    }
  };

  const updateSegment = (updated: SegmentDraft) => {
    setSegments(segments.map((s) => (s.id === updated.id ? updated : s)));
  };

  const deleteSegmentLocal = async (seg: SegmentDraft) => {
    if (!seg.isNew) {
      try {
        await deleteSegmentMutation.mutateAsync(seg.id);
      } catch {
        toast.error('Failed to delete segment');
        return;
      }
    }
    setSegments(segments.filter((s) => s.id !== seg.id));
  };

  const duplicateSegmentLocal = (seg: SegmentDraft) => {
    const copy: SegmentDraft = { ...seg, id: `new-${Date.now()}`, isNew: true };
    const idx = segments.findIndex((s) => s.id === seg.id);
    const next = [...segments];
    next.splice(idx + 1, 0, copy);
    setSegments(next);
  };

  const addSegment = (type: SegmentType) => {
    const draft: SegmentDraft = { id: `new-${Date.now()}`, type, name: segmentTypeConfig[type].label, isNew: true };
    setSegments([...segments, draft]);
  };

  const updateMeta = (updates: Partial<WorkoutTemplateResponse>) => {
    setMetaDraft((prev) => ({ ...prev, ...updates }));
  };

  if (isLoading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-48">
          <p className="text-xs text-muted-foreground font-mono">Loading workouts...</p>
        </div>
      </CoachLayout>
    );
  }

  const workout = selected ? { ...selected, ...metaDraft, segments } : null;

  return (
    <CoachLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Workout Builder</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Create and edit structured workouts with segments</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="gap-1.5 text-xs font-mono uppercase tracking-wider border border-border/20" onClick={handleCreate} disabled={createMutation.isPending}>
              <Plus className="h-3.5 w-3.5" /> New
            </Button>
            {workout && (
              <>
                <Button variant="ghost" size="sm" className="gap-1.5 text-xs font-mono uppercase tracking-wider border border-border/20" onClick={handleDuplicate} disabled={duplicateMutation.isPending}>
                  <Copy className="h-3.5 w-3.5" /> Duplicate
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-1.5 text-xs font-mono uppercase tracking-wider border border-border/20 text-destructive hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-card border-border/30">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-foreground">Delete Workout?</AlertDialogTitle>
                      <AlertDialogDescription className="text-muted-foreground">
                        <span className="font-medium text-foreground">"{workout.name}"</span><br />
                        This will permanently delete the workout template. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="text-xs font-mono uppercase tracking-wider">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs font-mono uppercase tracking-wider" onClick={handleDelete}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            <Button size="sm" className="gap-1.5 bg-accent/90 hover:bg-accent text-accent-foreground text-xs font-mono uppercase tracking-wider" onClick={handleSave} disabled={saving || !workout}>
              <Save className="h-3.5 w-3.5" /> Save
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-3">
            <div className="rounded-lg border border-border/15 bg-card/20 overflow-hidden">
              <div className="px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border/10">
                Templates ({templates.length})
              </div>
              <div className="p-2 space-y-1 max-h-[600px] overflow-y-auto">
                {templates.map((t) => {
                  const sport = sportConfig[t.sport as SportType];
                  return (
                    <button key={t.id} onClick={() => loadTemplate(t)}
                      className={cn('w-full text-left rounded-md px-3 py-2 transition-all', t.id === selectedId ? 'bg-accent/10 border border-accent/30' : 'hover:bg-card/50 border border-transparent')}>
                      <p className="text-xs text-foreground font-medium truncate">{t.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {sport && <span className="text-[8px] font-mono uppercase px-1 py-px rounded" style={{ background: `${sport.color}15`, color: sport.color }}>{sport.label}</span>}
                        <span className="text-[10px] text-muted-foreground">{t.duration}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-9 space-y-4">
            {workout ? (
              <>
                {/* Workout Header */}
                <div className="rounded-lg border border-border/15 bg-card/20 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {editingMeta ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          <div className="space-y-1 sm:col-span-2">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Name</label>
                            <input type="text" value={metaDraft.name ?? workout.name} onChange={e => updateMeta({ name: e.target.value })}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Sport</label>
                            <select value={metaDraft.sport ?? workout.sport} onChange={e => updateMeta({ sport: e.target.value as SportType })}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none capitalize">
                              {sportOptions.map(s => <option key={s} value={s}>{sportConfig[s].label}</option>)}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Stimulus</label>
                            <select value={metaDraft.stimulus ?? workout.stimulus ?? ''} onChange={e => updateMeta({ stimulus: e.target.value })}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none">
                              {stimulusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Duration</label>
                            <input type="text" value={metaDraft.duration ?? workout.duration ?? ''} onChange={e => updateMeta({ duration: e.target.value })}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" placeholder="e.g. 60 min" />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Difficulty</label>
                            <select value={metaDraft.difficulty ?? workout.difficulty ?? 'intermediate'} onChange={e => updateMeta({ difficulty: e.target.value as DifficultyLevel })}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none capitalize">
                              {difficultyOptions.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                          </div>
                          <div className="space-y-1 col-span-2 sm:col-span-3">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Tags (comma-separated)</label>
                            <input type="text" value={(metaDraft.tags ?? workout.tags).join(', ')} onChange={e => updateMeta({ tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" placeholder="e.g. tempo, intervals" />
                          </div>
                          <div className="space-y-1 col-span-2 sm:col-span-3">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Notes</label>
                            <textarea value={metaDraft.notes ?? workout.notes ?? ''} onChange={e => updateMeta({ notes: e.target.value || undefined })}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground placeholder:text-muted-foreground/40 focus:border-accent/40 focus:outline-none resize-none h-14" placeholder="Workout notes..." />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          {sportConfig[workout.sport as SportType] && (
                            <div className="h-8 w-1 rounded-full" style={{ background: sportConfig[workout.sport as SportType].color }} />
                          )}
                          <div className="flex-1">
                            <h2 className="text-base font-medium text-foreground">{workout.name}</h2>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{workout.stimulus}</span>
                              <span className="text-[9px] text-muted-foreground">·</span>
                              <span className="text-[9px] text-muted-foreground">{workout.duration}</span>
                              <span className="text-[9px] text-muted-foreground">·</span>
                              <span className="text-[9px] text-muted-foreground capitalize">{workout.difficulty}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className={cn('h-7 w-7 ml-2', editingMeta ? 'text-accent' : 'text-muted-foreground hover:text-foreground')}
                      onClick={() => setEditingMeta(!editingMeta)}>
                      {editingMeta ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  {!editingMeta && workout.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {workout.tags.map((tag) => (
                        <span key={tag} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-border/20 text-muted-foreground">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={segments.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {segments.map((seg) => (
                        <SegmentRow key={seg.id} segment={seg} onUpdate={updateSegment} onDelete={() => deleteSegmentLocal(seg)} onDuplicate={() => duplicateSegmentLocal(seg)} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                {segments.length === 0 && (
                  <div className="rounded-lg border border-dashed border-border/30 p-8 text-center">
                    <p className="text-xs text-muted-foreground">No segments yet. Add one below.</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {(Object.keys(segmentTypeConfig) as SegmentType[]).map((type) => {
                    const config = segmentTypeConfig[type];
                    return (
                      <Button key={type} variant="ghost" size="sm" className="gap-1.5 h-7 text-[10px] font-mono uppercase tracking-wider border border-border/15 hover:border-border/30" onClick={() => addSegment(type)}>
                        <Plus className="h-3 w-3" style={{ color: config.color }} />
                        {config.label}
                      </Button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-border/15 bg-card/20 p-12 text-center">
                <p className="text-sm text-muted-foreground">Select a template to edit</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </CoachLayout>
  );
}
